import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  IconButton,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Radio,
  RadioGroup,
  useDisclosure,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiSettings, FiDownload } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import Papa from 'papaparse';
import { useAuth } from '../../../contexts/AuthContext';
import { useDealsTable } from '../../../hooks/useDealsTable';
import { useVisibilityScope } from '../../../hooks/useVisibilityScope';
import { useSmartLists } from '../../../hooks/useSmartLists';
import { useCRMTablePreferences } from '../../../hooks/useCRMTablePreferences';
import usePostcardAccess from '../../../hooks/usePostcardAccess';
import CustomizableDealsTable from '../../../components/CRM/CustomizableDealsTable';
import DealsFilterToolbar from '../../../components/CRM/DealsFilterToolbar';
import TableCustomizationModal from '../../../components/CRM/TableCustomizationModal';
import BulkActionsBar from '../../../components/CRM/BulkActionsBar';
import { BulkStageModal, BulkTagModal, BulkAssignModal, BulkDealRecordScopeModal, BulkDeleteModal } from '../../../components/CRM/BulkModals';
import BulkPostcardModal from '../../../components/CRM/BulkPostcardModal';
import { ALL_COLUMNS, DEFAULT_PREFERENCES } from '../../../components/CRM/tableColumnDefinitions';
import { ALL_CONTACTS_COLUMNS } from '../../../components/CRM/contactsTableColumnDefinitions';
import WorkbenchQuickChips from './WorkbenchQuickChips';

/**
 * Deals lens of the workspace workbench — the My Deals table experience
 * (same hook, toolbar, table, select-all banner, and bulk save paths),
 * retargeted at the workspace:
 *  - row click opens /crm/workspace/:contactId?deal={id} (person-first)
 *  - property address is a DEFAULT column (address-first, per the ask)
 *  - column prefs live under their own Firestore key
 */

// Hide lost deals by default — same baseline as My Deals
const BASELINE_FILTERS = [{ field: 'stage', operator: 'not_in', value: ['deal-lost'] }];

// Address-first defaults: street address right after name + stage.
const WORKSPACE_DEALS_COLUMNS = [
  'name',
  'stage',
  'propertyAddress',
  'amount',
  'phone',
  'tags',
  'due',
  'lastTouched',
];
const WORKSPACE_DEALS_PREFERENCES = {
  ...DEFAULT_PREFERENCES,
  visibleColumns: WORKSPACE_DEALS_COLUMNS,
  columnOrder: WORKSPACE_DEALS_COLUMNS,
  sortBy: 'lastTouched',
  sortDirection: 'desc',
};

const DEAL_DATE_COLUMNS = new Set(['due', 'created', 'lastTouched', 'appDate', 'estClosingDate']);
const CONTACT_STATS_COLUMNS = new Set(['totalDeals', 'wonDeals', 'lifetimeValue', 'lastContact', 'score']);
const CONTACT_DATE_COLUMNS = new Set(['createdAt']);

export default function DealsWorkbench({
  currentUser,
  isCRMAdmin,
  selectedUserId,
  crmUsers,
  usersMap,
  searchTerm,
  onOpenPerson,
  onCountChange,
}) {
  const toast = useToast();
  const history = useHistory();
  const { Roles } = useAuth();
  const { canSendPostcards } = usePostcardAccess();
  const isDTCLoanOfficer = Roles?.includes('DTC Loan Officer');
  const boxBG = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  /* ---------------- Table preferences (isolated key) ---------------- */
  const {
    preferences: tablePreferences,
    updatePreferences,
    resetToDefaults: resetTablePrefs,
  } = useCRMTablePreferences(currentUser?.uid, {
    defaultPreferences: WORKSPACE_DEALS_PREFERENCES,
    preferencesKey: 'workspaceDealsTablePrefs',
  });

  /* ---------------- Server-side deals table ---------------- */
  // Multi-owner visibility: owned + team book-shares + record shares (+ company
  // records for admins). Admin with no user selected = All Users.
  const { scope: ownerScope } = useVisibilityScope({
    entityType: 'deal',
    targetUserId: isCRMAdmin ? (selectedUserId || null) : currentUser?.uid,
  });

  const {
    deals,
    totalCount,
    totalPages,
    currentPage,
    pageSize,
    loading,
    filters: activeFilters,
    sort: dealsSort,
    applyFilters: applyDealFilters,
    applySort: applyDealSort,
    setPage,
    setPageSize,
    setGlobalSearch,
    refresh: refreshDeals,
    fetchAllIds,
    fetchAllDeals,
  } = useDealsTable({
    ownerScope,
    pageSize: 25,
    initialSort: { field: 'lastTouched', direction: 'desc' },
    globalSearchTerm: searchTerm,
    initialFilters: BASELINE_FILTERS,
  });

  // Shell-driven search: push term changes into the hook (initial value was
  // seeded via globalSearchTerm above, so skip until it actually changes).
  const lastAppliedTermRef = useRef(searchTerm);
  useEffect(() => {
    if (lastAppliedTermRef.current === searchTerm) return;
    lastAppliedTermRef.current = searchTerm;
    setGlobalSearch(searchTerm);
  }, [searchTerm, setGlobalSearch]);

  // The count badge lives in the shell's control bar now
  useEffect(() => {
    onCountChange?.(totalCount);
  }, [totalCount, onCountChange]);

  /* ---------------- Smart lists (shared with My Deals) ---------------- */
  const {
    smartLists,
    personalLists,
    globalLists,
    selectedListId,
    setSelectedListId,
    createSmartList,
    updateSmartList,
    deleteSmartList,
    duplicateSmartList,
    setAsDefault: setSmartListAsDefault,
    unsetDefault: unsetSmartListDefault,
    loading: smartListsLoading,
  } = useSmartLists({ userId: currentUser?.uid, entityType: 'deals' });

  /* ---------------- Selection ---------------- */
  const [selectedDealIds, setSelectedDealIds] = useState([]);
  const [isAllDealsSelected, setIsAllDealsSelected] = useState(false);
  const [isSelectAllLoading, setIsSelectAllLoading] = useState(false);

  useEffect(() => {
    setSelectedDealIds([]);
    setIsAllDealsSelected(false);
  }, [activeFilters, searchTerm, selectedUserId]);

  const handleSelectionChange = (dealId) => {
    if (isAllDealsSelected) setIsAllDealsSelected(false);
    setSelectedDealIds((prev) =>
      prev.includes(dealId) ? prev.filter((id) => id !== dealId) : [...prev, dealId]
    );
  };

  const handleSelectAllMatchingFilters = async () => {
    setIsSelectAllLoading(true);
    try {
      const allIds = await fetchAllIds();
      setSelectedDealIds(allIds);
      setIsAllDealsSelected(true);
      toast({ title: `Selected ${allIds.length.toLocaleString()} deals`, status: 'success', duration: 2000 });
    } catch (error) {
      toast({ title: 'Error selecting deals', description: error.message, status: 'error', duration: 3000 });
    } finally {
      setIsSelectAllLoading(false);
    }
  };

  const handleClearAllSelection = () => {
    setSelectedDealIds([]);
    setIsAllDealsSelected(false);
  };

  const handleBulkSuccess = () => {
    handleClearAllSelection();
    refreshDeals();
  };

  /* ---------------- Row click → workspace (person-first) ---------------- */
  const handleDealClick = (deal) => {
    if (deal?.contactId) {
      onOpenPerson(deal.contactId, deal.id);
    } else if (deal?.id) {
      // No linked contact — the workspace can't frame it, use the classic page
      history.push(`/crm/deals/${deal.id}`);
    }
  };

  /* ---------------- CSV export (same as My Deals, admin only) ---------------- */
  const [isExporting, setIsExporting] = useState(false);
  const [exportColumnMode, setExportColumnMode] = useState('visible');
  const { isOpen: isExportOpen, onOpen: onExportOpen, onClose: onExportClose } = useDisclosure();

  const visibleColumns = tablePreferences.visibleColumns || [];

  const handleExportCSV = async () => {
    setIsExporting(true);
    onExportClose();
    try {
      const exportDeals = await fetchAllDeals();
      const dealColumnsToExport = exportColumnMode === 'visible'
        ? visibleColumns
        : ALL_COLUMNS.map((c) => c.id);

      const data = exportDeals.map((deal) => {
        const row = {};
        dealColumnsToExport.forEach((colId) => {
          const column = ALL_COLUMNS.find((c) => c.id === colId);
          // Admin-only columns (record scope) never leave via non-admin exports
          if (!column || (column.adminOnly && !isCRMAdmin)) return;
          let value = column.accessor(deal);
          if (DEAL_DATE_COLUMNS.has(colId)) {
            value = value > 0 ? moment(value * 1000).format('YYYY-MM-DD') : '';
          } else if (colId === 'owner') {
            value = usersMap.get(value) || value || 'Unassigned';
          }
          row[column.label] = value;
        });

        if (exportColumnMode === 'allWithContact') {
          ALL_CONTACTS_COLUMNS.forEach((column) => {
            if (CONTACT_STATS_COLUMNS.has(column.id)) return;
            if (column.adminOnly && !isCRMAdmin) return;
            let value = deal.contact ? column.accessor(deal.contact) : '';
            if (column.id === 'owner') {
              value = usersMap.get(value) || value || 'Unassigned';
            } else if (CONTACT_DATE_COLUMNS.has(column.id)) {
              value = value > 0 ? moment(value * 1000).format('YYYY-MM-DD') : '';
            }
            row[`Contact: ${column.label}`] = value;
          });
        }
        return row;
      });

      const csv = Papa.unparse(data);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `workspace-deals-export-${moment().format('YYYY-MM-DD')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({ title: 'Export complete', description: `Exported ${data.length.toLocaleString()} deals`, status: 'success', duration: 3000 });
    } catch (error) {
      toast({ title: 'Export failed', description: error.message, status: 'error', duration: 5000 });
    } finally {
      setIsExporting(false);
    }
  };

  /* ---------------- Modals ---------------- */
  const { isOpen: isTableCustomOpen, onOpen: onTableCustomOpen, onClose: onTableCustomClose } = useDisclosure();
  const { isOpen: isBulkStageOpen, onOpen: onBulkStageOpen, onClose: onBulkStageClose } = useDisclosure();
  const { isOpen: isBulkTagOpen, onOpen: onBulkTagOpen, onClose: onBulkTagClose } = useDisclosure();
  const { isOpen: isBulkAssignOpen, onOpen: onBulkAssignOpen, onClose: onBulkAssignClose } = useDisclosure();
  const { isOpen: isBulkDeleteOpen, onOpen: onBulkDeleteOpen, onClose: onBulkDeleteClose } = useDisclosure();
  const { isOpen: isBulkScopeOpen, onOpen: onBulkScopeOpen, onClose: onBulkScopeClose } = useDisclosure();
  const { isOpen: isBulkPostcardOpen, onOpen: onBulkPostcardOpen, onClose: onBulkPostcardClose } = useDisclosure();

  return (
    <Box bg={boxBG} borderWidth="1px" borderColor={borderColor} borderRadius="xl" p={{ base: 3, md: 5 }} shadow="sm">
      <VStack align="stretch" spacing={4}>
        {/* One-click presets */}
        <WorkbenchQuickChips
          entityType="deals"
          filters={activeFilters}
          onApply={(chipFilters) => {
            setSelectedListId(null);
            applyDealFilters(chipFilters || BASELINE_FILTERS);
          }}
        />

        {/* Full My Deals filter toolbar (stage/tags/at-risk + advanced + smart lists) */}
        <DealsFilterToolbar
          filters={activeFilters}
          onFiltersChange={applyDealFilters}
          smartLists={smartLists}
          personalLists={personalLists}
          globalLists={globalLists}
          selectedSmartListId={selectedListId}
          onSelectSmartList={(listId, listFilters, listSortBy, listSortDirection, listVisibleColumns) => {
            setSelectedListId(listId);
            if (listId) {
              applyDealFilters(listFilters || []);
              if (listSortBy) {
                applyDealSort(listSortBy, listSortDirection || 'desc');
                updatePreferences({ sortBy: listSortBy, sortDirection: listSortDirection || 'desc' });
              }
              if (listVisibleColumns && listVisibleColumns.length > 0) {
                updatePreferences({ visibleColumns: listVisibleColumns, columnOrder: listVisibleColumns });
              }
            } else {
              applyDealFilters(BASELINE_FILTERS);
            }
          }}
          onCreateSmartList={createSmartList}
          onUpdateSmartList={updateSmartList}
          onDeleteSmartList={deleteSmartList}
          onDuplicateSmartList={duplicateSmartList}
          onSetDefaultSmartList={setSmartListAsDefault}
          onUnsetDefaultSmartList={unsetSmartListDefault}
          currentSort={{ field: dealsSort?.field || tablePreferences.sortBy, direction: dealsSort?.direction || tablePreferences.sortDirection }}
          visibleColumns={visibleColumns}
          loading={smartListsLoading}
          rightSlot={
            <HStack spacing={2}>
              {isCRMAdmin && (
                <Button size="sm" leftIcon={<FiDownload />} variant="outline" onClick={onExportOpen} isLoading={isExporting}>
                  Export
                </Button>
              )}
              <Tooltip label="Customize table columns">
                <IconButton
                  icon={<FiSettings />}
                  aria-label="Customize table columns"
                  size="sm"
                  variant="outline"
                  onClick={onTableCustomOpen}
                />
              </Tooltip>
            </HStack>
          }
        />

        {/* The My Deals table, rerouted to the workspace */}
        <CustomizableDealsTable
          deals={deals}
          totalCount={totalCount}
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          sortBy={dealsSort?.field || tablePreferences.sortBy}
          sortDirection={dealsSort?.direction || tablePreferences.sortDirection}
          onSortChange={(field, direction) => {
            applyDealSort(field, direction);
            updatePreferences({ sortBy: field, sortDirection: direction });
          }}
          loading={loading}
          visibleColumns={visibleColumns}
          columnOrder={tablePreferences.columnOrder}
          selectedDeals={selectedDealIds}
          onSelectionChange={handleSelectionChange}
          onSelectAllMatchingFilters={handleSelectAllMatchingFilters}
          isAllMatchingFiltersSelected={isAllDealsSelected}
          isSelectAllLoading={isSelectAllLoading}
          onClearAllSelection={handleClearAllSelection}
          onDealClick={handleDealClick}
          usersMap={usersMap}
          ownerChipViewerId={isCRMAdmin ? null : currentUser?.uid}
        />
      </VStack>

      {/* Bulk bar — same save paths as My Deals */}
      {selectedDealIds.length > 0 && (
        <BulkActionsBar
          count={selectedDealIds.length}
          onChangeStage={onBulkStageOpen}
          onAddTag={onBulkTagOpen}
          onAssign={onBulkAssignOpen}
          onDelete={onBulkDeleteOpen}
          onSendPostcard={canSendPostcards ? onBulkPostcardOpen : null}
          onClear={handleClearAllSelection}
          hasAssignedDeals={deals.filter((d) => selectedDealIds.includes(d.id)).some((d) => d.ownerId)}
          isAllSelected={isAllDealsSelected}
          onSetRecordScope={isCRMAdmin ? onBulkScopeOpen : null}
        />
      )}

      {/* Bulk modals (existing, untouched) */}
      <BulkStageModal isOpen={isBulkStageOpen} onClose={onBulkStageClose} selectedDeals={selectedDealIds} onSuccess={handleBulkSuccess} isDTCLoanOfficer={isDTCLoanOfficer} />
      <BulkDealRecordScopeModal isOpen={isBulkScopeOpen} onClose={onBulkScopeClose} selectedDeals={selectedDealIds} onSuccess={handleBulkSuccess} />
      <BulkTagModal isOpen={isBulkTagOpen} onClose={onBulkTagClose} selectedDeals={selectedDealIds} onSuccess={handleBulkSuccess} />
      <BulkAssignModal
        isOpen={isBulkAssignOpen}
        onClose={onBulkAssignClose}
        selectedDeals={selectedDealIds}
        selectedDealObjects={deals.filter((d) => selectedDealIds.includes(d.id))}
        crmUsers={crmUsers}
        onSuccess={handleBulkSuccess}
      />
      <BulkDeleteModal isOpen={isBulkDeleteOpen} onClose={onBulkDeleteClose} selectedDeals={selectedDealIds} onSuccess={handleBulkSuccess} />
      <BulkPostcardModal isOpen={isBulkPostcardOpen} onClose={onBulkPostcardClose} recipientType="deals" ids={selectedDealIds} onSuccess={handleBulkSuccess} />

      <TableCustomizationModal
        isOpen={isTableCustomOpen}
        onClose={onTableCustomClose}
        visibleColumns={tablePreferences.visibleColumns}
        columnOrder={tablePreferences.columnOrder}
        onSave={(updates) => {
          updatePreferences(updates);
          toast({ title: 'Table customization saved', status: 'success', duration: 2000 });
        }}
        onReset={async () => {
          await resetTablePrefs();
          toast({ title: 'Reset to defaults', status: 'success', duration: 2000 });
        }}
      />

      {/* Export options (same three modes as My Deals) */}
      <Modal isOpen={isExportOpen} onClose={onExportClose} size="sm" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Export Deals</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="gray.600" mb={4}>
              {totalCount.toLocaleString()} deals match your current filters. Choose which columns to include:
            </Text>
            <RadioGroup value={exportColumnMode} onChange={setExportColumnMode}>
              <VStack align="start" spacing={3}>
                <Radio value="visible">
                  <Text fontSize="sm" fontWeight="medium">Visible columns only</Text>
                  <Text fontSize="xs" color="gray.500">{visibleColumns.length} columns currently shown</Text>
                </Radio>
                <Radio value="all">
                  <Text fontSize="sm" fontWeight="medium">All deal columns</Text>
                  <Text fontSize="xs" color="gray.500">{ALL_COLUMNS.length} deal columns including hidden fields</Text>
                </Radio>
                <Radio value="allWithContact">
                  <Text fontSize="sm" fontWeight="medium">All deal + contact columns</Text>
                  <Text fontSize="xs" color="gray.500">{ALL_COLUMNS.length + ALL_CONTACTS_COLUMNS.length} columns from both tables</Text>
                </Radio>
              </VStack>
            </RadioGroup>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onExportClose} isDisabled={isExporting}>Cancel</Button>
            <Button colorScheme="blue" onClick={handleExportCSV} isLoading={isExporting} loadingText="Exporting..." leftIcon={<FiDownload />}>
              Export CSV
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
